import type { Address, InsuranceTerms, Rate, ShipmentQueueItem } from '../../types';
import { recipientPhoneIssue } from '../../utils/recipientPhone';
import { productNameIssue } from '../../utils/productName';

export interface BulkItem extends ShipmentQueueItem {
  rates?: Rate[];
  /** Set durably before dispatching a billable request; never cleared by a retry. */
  purchaseAttempted?: boolean;
  normalizedToAddress?: Address;
}

export function isFrozenPurchase(item: BulkItem | undefined): boolean {
  return Boolean(item?.previewShipmentId) && item?.purchaseAttempted !== false;
}

export function isUsableRate(rate: Rate): boolean {
  return Boolean(rate && typeof rate.id === 'string' && rate.id.trim()
    && typeof rate.carrier === 'string' && rate.carrier.toUpperCase().startsWith('FEDEX')
    && typeof rate.rate === 'string' && rate.rate.trim()
    && Number.isFinite(Number(rate.rate)) && Number(rate.rate) >= 0
    && typeof rate.currency === 'string' && /^[A-Za-z]{3}$/.test(rate.currency));
}

export function isReadyQuote(item: BulkItem): boolean {
  return item.status !== 'completed' && typeof item.previewShipmentId === 'string' && Boolean(item.previewShipmentId.trim()) && Boolean(item.selectedRate && isUsableRate(item.selectedRate));
}

/** Reject corrupt recovery data without reinterpreting or overwriting its handles. */
export function hasUsableQueueEntries(values: unknown): values is Partial<BulkItem>[] {
  if (!Array.isArray(values) || !values.length) return false;
  const ids = new Set<string>();
  return values.every(item => {
    const id = item?.address?.id;
    if (typeof id !== 'string' || !id.trim() || ids.has(id)) return false;
    ids.add(id);
    if (item.previewShipmentId !== undefined && (typeof item.previewShipmentId !== 'string' || !item.previewShipmentId.trim() || !isUsableRate(item.selectedRate))) return false;
    if (item.rates !== undefined && (!Array.isArray(item.rates) || !item.rates.every(isUsableRate))) return false;
    if (item.previewShipmentId && item.rates && !item.rates.some((rate: Rate) => rate.id === item.selectedRate?.id && rate.rate === item.selectedRate?.rate && rate.currency === item.selectedRate?.currency)) return false;
    if (item.purchaseAttempted !== undefined && typeof item.purchaseAttempted !== 'boolean') return false;
    try { insuranceTerms(item.insurance); } catch { return false; }
    return true;
  });
}

/** Keep currencies separate and exclude unresolved purchases from confirmed totals. */
export function postageTotals(items: BulkItem[], confirmedOnly = false): Array<{ currency: string; amount: number }> {
  const sums = new Map<string, number>();
  for (const item of items) {
    if (confirmedOnly && item.status !== 'completed') continue;
    const rate = confirmedOnly ? item.result?.rate ?? item.selectedRate : item.selectedRate;
    if (!rate || !isUsableRate(rate)) continue;
    const currency = rate.currency.toUpperCase();
    sums.set(currency, (sums.get(currency) ?? 0) + Number(rate.rate));
  }
  return [...sums].map(([currency, amount]) => ({ currency, amount }));
}

export function insuranceTerms(value: InsuranceTerms | null | undefined): InsuranceTerms | null {
  if (!value) return null;
  if (typeof value.amount !== 'string' || !value.amount.trim() || !Number.isFinite(Number(value.amount)) || Number(value.amount) <= 0 || typeof value.currency !== 'string' || !/^[A-Za-z]{3}$/.test(value.currency)) {
    throw new Error('The carrier returned invalid insurance terms. No label was purchased.');
  }
  return { amount: value.amount, currency: value.currency };
}

export function addressIssue(address: Address, senderCountry?: string, phoneRequired = true): string | null {
  if (!address.name?.trim()) return 'Enter a recipient name.';
  if (!address.street1?.trim()) return 'Enter a street address.';
  if (!address.city?.trim()) return 'Enter a city.';
  if (!address.state?.trim()) return 'Choose a state or province.';
  if (!address.zip?.trim()) return 'Enter a ZIP or postal code.';
  if (senderCountry && (address.country || 'US') !== senderCountry) return 'This destination requires customs information and cannot be purchased here.';
  if (address.gift === 'Tshirt' && !address.tshirt_size) return 'Choose a T-shirt size.';
  const phoneIssue = phoneRequired && recipientPhoneIssue(address.phone);
  return phoneIssue || productNameIssue(address.product_name);
}
